import React from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import { LoadingSkeleton } from '@eventbrite/legacy-infra.loading-skeleton';
import styles from './OrganizerStats.module.scss';
import type { OrganizerWithFollowStatus } from '@/src/listings/components/OrganizerCard/types';
import { useOrganizerProfileOptional } from '@/src/listings/components/_shared/contexts/OrganizerProfileContext';
import { formatCount, MAX_DISPLAYED_EVENTS } from '@/src/listings/utils/formatCount';

const Stat = ({ label, data, divider }: { label: string; data: string | number; divider: boolean }) => {
  const statUnitClasses = classNames(styles.stat, { [styles.statWithDivider]: divider });

  return (
    <div className={statUnitClasses}>
      <span className={styles.label}>{label}</span>
      <span className={styles.data}>{data}</span>
    </div>
  );
};

interface OrganizerStatsProps {
  organizer: OrganizerWithFollowStatus;
}

export const OrganizerStats: React.FC<OrganizerStatsProps> = ({ organizer }) => {
  const { formatMessage } = useIntl();
  const { loading: profileLoading, hideTotalEvents } = useOrganizerProfileOptional();

  const NO_AVAILABLE_DATA = '--';

  const numberOfFollowers = organizer.followStatus?.numFollowers || NO_AVAILABLE_DATA;
  const numberOfEvents = organizer.numEvents;
  const hasNumberOfEvents = numberOfEvents != null && !hideTotalEvents;
  const hostingSinceText =
    organizer.hostingSince !== undefined && organizer.hostingSince > 0
      ? `${organizer.hostingSince} ${formatMessage({ id: 'years' })}`
      : NO_AVAILABLE_DATA;

  if (profileLoading) {
    return (
      <div className={styles.wrapper} data-testid="organizer-stats">
        <div data-testid="organizer-stats-skeleton" aria-busy="true">
          <LoadingSkeleton height={44} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper} data-testid="organizer-stats">
      <Stat label={formatMessage({ id: 'Followers' })} data={numberOfFollowers} divider={false} />
      {hasNumberOfEvents && (
        <Stat
          label={formatMessage({ id: 'Events' })}
          data={formatCount(numberOfEvents!, MAX_DISPLAYED_EVENTS)}
          divider={true}
        />
      )}
      <Stat label={formatMessage({ id: 'Hosting' })} data={hostingSinceText} divider={true} />
    </div>
  );
};
